import React from 'react';
import { VignetteProps } from '../types';

const COLORS = ['#AB72ED', '#E516C5', '#F4982B', '#1FA259', '#1ABCE1', '#EC4A85'];
const BADGE_SIZE = 62;
const BADGE_HALF = BADGE_SIZE / 2;

const keyframes = `
@keyframes shepherdSmilePop {
  from { transform: scale(0) rotate(-14deg); }
  to { transform: scale(1) rotate(0); }
}
@keyframes shepherdBurst {
  0% { opacity: 0; transform: translate(0,0) scale(.4); }
  20% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--bx), var(--by)) scale(1); }
}
`;

// Item H: the smiley sits as a badge seated on the step card's top edge
// (half in, half out) instead of floating independently, so it never
// collides with the card. Falls back to the original viewport-centered spot
// when the card hasn't measured yet. Both branches size the wrapper to
// exactly BADGE_SIZE so the burst chips (positioned at 50%/50% of the
// wrapper) radiate from the true badge center either way.
const FinaleBurst: React.FC<VignetteProps> = ({ cardRect, reduced }) => {
  const wrapperStyle: React.CSSProperties = cardRect
    ? {
      position: 'fixed',
      top: cardRect.top - BADGE_HALF,
      left: cardRect.left + cardRect.width / 2 - BADGE_HALF,
      width: BADGE_SIZE,
      height: BADGE_SIZE,
      pointerEvents: 'none',
    }
    : {
      position: 'fixed',
      left: '50%',
      top: 'calc(50% - 130px)',
      width: BADGE_SIZE,
      height: BADGE_SIZE,
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
    };

  return (
    <div style={wrapperStyle} aria-hidden="true">
      <style>{keyframes}</style>
      <div
        style={{
          width: BADGE_SIZE,
          height: BADGE_SIZE,
          boxSizing: 'border-box',
          borderRadius: '50%',
          background: 'linear-gradient(144deg,#AB72ED,#744DA1)',
          border: '3px solid #F7F5F8',
          boxShadow: '2px 2px 8px rgba(24,0,100,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: reduced ? 'none' : 'shepherdSmilePop .6s cubic-bezier(.2,.9,.3,1) both',
        }}
      >
        {/* The face is lifted straight off assets/onboarding/doq-ready-smiley.svg
            (the one on the "your DOQ is ready" success screen) - same arched
            eyes, same wide smile, same 6-unit stroke, drawn in white so it
            reads on the badge. Catherine asked for the finale to use the face
            people already know from that screen (869eeq68p). The badge keeps
            its own outer circle, so only the eyes and mouth are borrowed and
            the source circle is left off. */}
        <svg width="36" height="36" viewBox="84 112 180 168" fill="none" stroke="#fff" strokeWidth="10" strokeLinecap="round">
          <path d="M161 160.936C161 144.919 148.24 131.936 132.5 131.936C116.76 131.936 104 144.919 104 160.936" />
          <path d="M244 160.936C244 144.919 231.24 131.936 215.5 131.936C199.76 131.936 187 144.919 187 160.936" />
          <path d="M104 191.936C104 228.939 135.34 258.936 174 258.936C212.66 258.936 244 228.939 244 191.936" />
        </svg>
      </div>
      {!reduced && COLORS.map((col, k) => {
        const ang = (k / COLORS.length) * Math.PI * 2;
        return (
          <span
            key={col}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: col,
              opacity: 0,
              ['--bx' as string]: `${Math.round(Math.cos(ang) * 78)}px`,
              ['--by' as string]: `${Math.round(Math.sin(ang) * 52)}px`,
              animation: `shepherdBurst .9s cubic-bezier(.2,.9,.3,1) ${120 + k * 60}ms both`,
            }}
          />
        );
      })}
    </div>
  );
};

export default FinaleBurst;
